import i18n from 'i18next';

import { Text } from '@components';
import ConditionsDisplay from '@components/DraggableTable/DraggableTableCell/ConditionTableCell';
import { CellType } from '@enums';
import type { Column } from '@types';
import { getColumnSizes } from '@utils';
import { AnimatePresence, motion } from 'framer-motion';

const getOptionsLabel = ({ options, value }) => {
  return options.find((item) => item.value === value.cdn_global_waf_rule_id)?.label ?? '';
};

export const getCdnWAFWhitelistColumns = ({ options }): Column[] => [
  { label: i18n.t('configuration:tables.id'), value: 'id', multiLines: true, ...getColumnSizes(70) },
  {
    label: i18n.t('configuration:tables.whiteListName'),
    value: 'name',
    multiLines: true,
    ...getColumnSizes(130),
  },
  {
    label: i18n.t('configuration:tables.rulesAndCondition'),
    value: 'conditions',
    cellType: CellType.Custom,
    multiLines: true,
    ...getColumnSizes(160),
    format: (currentData, fullData) => {
      const hasRules = fullData?.rulesets.length > 0;

      return (
        <div className="space-y-4 mb-1.5">
          <div>
            <Text $level={4} $customizeColor className="text-theme-primary-main mb-2.5 font-medium">
              {i18n.t('configuration:tables.conditions')}
            </Text>

            <ConditionsDisplay data={fullData?.conditions} />
          </div>

          {hasRules && (
            <>
              <Text $level={4} $customizeColor className="text-theme-primary-main mb-2.5 font-medium">
                {i18n.t('configuration:forms.waf.whiteList.skipWafRules')}
              </Text>
              <div className="mt-3 relative pl-3 space-y-1.5">
                {fullData?.rulesets.map((value, valueIndex) => {
                  const isLast = valueIndex === fullData.rulesets.length - 1;

                  return (
                    <div key={valueIndex} className="relative">
                      {!isLast && (
                        <div
                          className="absolute left-0 top-2 w-px bg-gray-200"
                          style={{
                            height: 'calc(100% + 0.375rem)',
                          }}
                        ></div>
                      )}

                      <AnimatePresence>
                        <motion.div
                          key={valueIndex}
                          className="flex items-start gap-2 w-full relative pb-1.5"
                          initial={{ opacity: 0, x: -5 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -5 }}
                          transition={{ duration: 0.15, delay: valueIndex * 0.03 }}
                        >
                          <div className="w-3 h-px mt-2 bg-theme-primary-main/20 flex-shrink-0"></div>
                          <div className="max-w-full overflow-hidden">
                            <Text $customizeColor $level={5} className="text-gray-600 text-wrap block">
                              {getOptionsLabel({ options, value })}
                            </Text>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      );
    },
  },
  {
    label: i18n.t('configuration:tables.action'),
    value: 'actions',
    cellType: CellType.Action,
    ...getColumnSizes(90),
    multiLines: true,
  },
];
